import { effect, inject, Injectable, signal, Injector } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { RouterStore } from '@route/data-access';
import { selectFromMetadata } from '../entities/odata.model';
import { AuthStore } from '@auth/data-access';
import { ApiUrlsType, APP_CONFIG } from '@application/config';
import { filter, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { CustomStore, LoadOptions } from 'devextreme/common/data';
import { DX_PERMISSION_GUARD_CONFIG_TOKEN, DxPermissionGuardConfig } from '../entities/dx-permission-guard.model';
import { DateTimeService } from '@application/data-access';
import { DateTime } from 'luxon';

export interface IDxLookupConfig {
  url: keyof ApiUrlsType | string;
  metadata: string;
  key?: string;
  keyType?: string;
  params?: object;
  selectedItem?: any;
}

/**
 * Interface for visible column configuration
 */
export type TVisibleColumn = {
  dataField: string;
  dataType: 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime';
};

export interface IDateFilter {
  field: string;
  startDate: Date;
  endDate: Date;
}

export type TAggregate = string | { [field: string]: string } | string[];

type TRequest = {
  url: string;
  async: boolean;
  method: string;
  timeout: number;
  params: any;
  payload: any;
  headers: any;
};

@Injectable({ providedIn: 'root' })
export class DxOdataStoreService {
  private routeParams: any;
  private readonly injector = inject(Injector);
  private readonly routeStore = inject(RouterStore);
  private readonly http = inject(HttpClient);
  private readonly url = inject(APP_CONFIG).apiUrl;
  private readonly dateTimeService = inject(DateTimeService);

  constructor() {
    effect(() => {
      this.routeParams = this.routeStore.getCurrentRouteParams();
    });
  }

  public fromOdata<T>({
    url,
    metadata,
    key,
    keyType,
    params,
    useOptionsMethod = false,
    mode = 'raw',
    permissionGuard,
    selectedItem,
  }: {
    url: keyof ApiUrlsType | string;
    metadata: string;
    key?: string;
    keyType?: string;
    params?: any;
    useOptionsMethod?: boolean;
    mode?: 'raw' | 'lookup';
    permissionGuard?: DxPermissionGuardConfig;
    selectedItem?: any
  }): DataSource<T> {
    const request = signal<TRequest>(null);

    const odataDataSource: DataSource<T> = new DataSource<T>({
      store: new ODataStore<T>({
        url: url + `/${metadata}`,
        key: key,
        version: 4,
        keyType: keyType ?? 'Guid',
        beforeSend: (req) => {
          if (useOptionsMethod) {
            req.method = 'OPTIONS';
          }

          if (req.url.includes('(')) {
            const url = req.url.split('(');
            req.url = url[0];
            req.params['$filter'] = `${key} eq ${url[1].replace(')', '')}`;
          }

          const authStore = this.injector.get(AuthStore);
          req.headers = {
            Authorization: 'Bearer ' + authStore.getAuthData()?.Token,
          };
          if (selectFromMetadata.has(metadata) && !req.params['$select']) {
            req.params['$select'] = selectFromMetadata.get(metadata);
          }

          if (req.params?.$filter) {
            req.params.$filter = this.adjustODataFilterDatesToUtc(req.params.$filter);
          }

          const normalizeLookup = (): string => {
            if (req.params['$filter'].includes(key)) {
              return req.params['$filter']
            }
            return `(${req.params['$filter']}) and ${params['$filter']}`
          };


          if (params) {
            if (req.params['$filter'] && params['$filter']) {
              req.params = {
                ...req.params,
                ...params,
                ['$filter']: mode === 'lookup' ? normalizeLookup() : `(${req.params['$filter']}) and ${params['$filter']}`,
              };
            } else {
              req.params = { ...req.params, ...params };
            }

            if (params['$apply']) {
              req.params = { $apply: params['$apply'] };
            }
          }
          
          request.set(req);
        },
      }),
    });

    const customDataSource: DataSource<T> = new DataSource<T>({
      store: new CustomStore<T>({
        key,
        byKey: (keyValue: any, extraOptions?: LoadOptions<T>) => {
          return selectedItem ? Promise.resolve([selectedItem]) : this.getData(odataDataSource, { filter: [key, '=', keyValue] });
        },
        load: (options) => {
          const dataPromise = this.getData(odataDataSource, options);
          const summaryPromise = this.getSummary({
            url: request()?.url,
            filter: request()?.params?.['$filter'],
            notOdataParams: Object.fromEntries(Object.entries(request()?.params || {}).filter(([key]) => !key.startsWith('$')))
          }, options);

          return Promise.all([dataPromise, summaryPromise]).then(([data, summary]) => {
              return {
                totalCount: odataDataSource.totalCount(),
                data: this.formatUtcDatesInData(data),
                summary
              }
          }).catch((error) => {
            this.errorHandler(error, customDataSource);

            return {
                totalCount: 0,
                data: [],
                summary: []
              }
          });
        },
        errorHandler: (error) => this.errorHandler(error, customDataSource)
      }),
    })

    if (permissionGuard) {
      Object.defineProperty(customDataSource, DX_PERMISSION_GUARD_CONFIG_TOKEN, {
        value: permissionGuard,
        enumerable: false,
        configurable: true,
      });
    }

    return customDataSource;
  };

  private adjustODataFilterDatesToUtc(filter: string): string {
    const userZone = this.dateTimeService.getUserZone();
  
    return filter.replace(
      /([\w/.]+)\s+(ge|le|gt|lt|eq|ne)\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z)/g,
      (fullMatch, attribute, operator, isoDate) => {
        // If the attribute does not contain 'UTC', do not alter it
        if (!attribute.toUpperCase().includes('UTC')) {
          return fullMatch;
        }
  
        try {
          const localDate = DateTime.fromISO(
            isoDate.replace('Z', ''),
            { zone: userZone }
          );
  
          if (!localDate.isValid) {
            return fullMatch;
          }
  
          const utcDate = localDate.toUTC().toISO({
            suppressMilliseconds: false,
            includeOffset: false,
            format: 'extended'
          });
  
          return `${attribute} ${operator} ${utcDate}Z`;
        } catch {
          return fullMatch;
        }
      }
    );
  }
  

  private async getSummary<T>({ url, filter, notOdataParams }, { totalSummary }: LoadOptions<T>): Promise<number[]> {
    if (!totalSummary) return [];
    const totalSummaryArray = Array.isArray(totalSummary) ? totalSummary : [totalSummary]
    return await new Promise(resolve => {
      let $apply = '';
      if (filter) $apply += `filter(${filter})/`;
      const aggregates = totalSummaryArray.map(s => `${s['selector']} with ${s['summaryType']} as ${s['selector']}`);
      $apply += `aggregate(${aggregates.join(', ')})`;

      this.http.get(url, { params: { ...notOdataParams, $apply }, withCredentials: true }).subscribe({
        next: (response) => {
          if ("@odata.context" in response) response = response?.["value"];
          const summary = totalSummaryArray.map(s => response?.[0]?.[s['selector']] || 0);
          resolve(summary);
        },
        error: () => resolve([])
      })
    })
  }

  private async getData<T>(dataSource: DataSource<T, any>, options: LoadOptions<T>) {
    const {
      filter,
      requireTotalCount,
      searchExpr,
      searchOperation,
      searchValue,
      select,
      skip,
      sort,
      take,
      group,
      totalSummary,
      groupSummary,
      userData,
      parentIds,
      requireGroupCount,
      customQueryParams,
      startDate,
      endDate,
      expand,
    } = options;
    
    
    dataSource.group(group || null);
    dataSource.select(select || null);
    dataSource.sort(sort || null);
    dataSource.searchValue(searchValue || null);
    dataSource.requireTotalCount(requireTotalCount || null);
    dataSource.searchExpr(searchExpr || null);
    dataSource.searchOperation(searchOperation || null);
    dataSource.filter(filter || null);
    dataSource.paginate(!!take);
    dataSource.pageSize(take || null);
    dataSource.pageIndex((skip || 0) / (take || 1));
    return await dataSource.load();
  }

  private errorHandler<T>(error, customDataSource: DataSource<T, any>) {
    if (error.httpStatus === 401) {
      const authStore = this.injector.get(AuthStore);
      const currentToken = authStore.getAuthData()?.Token;
      authStore
        .observableAuthData()
        .pipe(
          filter(
            (authData) =>
              !!authData?.Token && authData.Token !== currentToken
          ),
          take(1)
        )
        .subscribe(() => {
          customDataSource.reload();
        });
      authStore.refreshToken();
    }
  }


  /**
   * Recursively converts all UTC dates to local timezone in the data structure.
   * ODataStore automatically converts ISO strings to Date objects treating them as UTC.
   * This method adjusts those Date objects to reflect the user's local timezone.
   */
  private formatUtcDatesInData<T>(data: T): T {
    if (!data) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.formatUtcDatesInData(item)) as T;
    }

    if (typeof data !== 'object' || data instanceof Date) {
      return data;
    }

    const result = { ...data } as T;

    for (const [key, value] of Object.entries(data)) {
      if (key.toLowerCase().includes('utc')) {
        if (value instanceof Date) {
  
          const userZone = this.dateTimeService.getUserZone();

          const utcDate = DateTime.fromObject({
            year: value.getFullYear(),
            month: value.getMonth() + 1,
            day: value.getDate(),
            hour: value.getHours(),
            minute: value.getMinutes(),
            second: value.getSeconds(),
            millisecond: value.getMilliseconds(),
          }, { zone: 'utc' });

          const localDate = DateTime.fromObject(utcDate.setZone(userZone).toObject());

          (result as any)[key] = localDate.toJSDate();
        } 
      } 
    }

    return result;
  }
}
